import { getToken } from "../../common/environment_config.es6";

import { apiGet, authedFetch } from "../../common/http_service";

export class ImagePromptLibraryService {
  static async getImagePromptLibrary(params = {}) {
    const {
      limit = 20,
      offset = 0,
      orderBy = "createdAt",
      orderDirection = "DESC",
      isSceneStyle,
      engine,
    } = params;

    const baseUrl = window.ideaLibraryRootUrl || "";
    let url = `${baseUrl}api/image-prompt-library?limit=${limit}&offset=${offset}&orderBy=${orderBy}&orderDirection=${orderDirection}`;
    if (isSceneStyle) url += "&isSceneStyle=true";
    // Engine bazlı filtre YALNIZCA sahne stilleri (isSceneStyle=true) için uygulanır.
    // Normal / sahne-dışı stiller (isSceneStyle=false) engine BAĞIMSIZDIR — onlara
    // engine filtresi eklenmez. Backend katı filtre uygular (`engines @> [engine]`):
    // sahne stilinde yalnızca bu aideaEngine'e açıkça atanmış olanlar döner.
    if (isSceneStyle && engine !== undefined && engine !== null) {
      url += `&engine=${engine}`;
    }

    try {
      const response = await apiGet(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        return await response.json();
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching image prompt library:", error);
      throw error;
    }
  }

  static async generateImageWithText(params, callback) {
    const {
      prompt,
      provider = "gemini-nano",
      orientation = "square",
      textOverlays = [],
      customStyle,
      negativePrompt,
      imagePromptLibraryId,
      idealStudioTrainingId,
      resolution,
    } = params;

    const apiUrl = process.env.MW_API_URL;
    if (!apiUrl) {
      if (callback) callback(false, "API URL not configured", null);
      return;
    }

    try {
      const body = { prompt, provider, orientation, textOverlays };
      if (customStyle) body.customStyle = customStyle;
      if (negativePrompt) body.negativePrompt = negativePrompt;
      if (imagePromptLibraryId !== undefined) body.imagePromptLibraryId = imagePromptLibraryId;
      if (idealStudioTrainingId) body.idealStudioTrainingId = idealStudioTrainingId;
      if (resolution) body.resolution = resolution;

      const response = await authedFetch(`${apiUrl}api/image-editing/generate-image-with-text`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errBody = null;
        try {
          errBody = await response.json();
        } catch {
          /* non-JSON body */
        }
        const errorMessage = errBody?.message || `HTTP error! status: ${response.status}`;
        if (callback) callback(false, errorMessage, null, errBody);
        return;
      }

      const { jobId } = await response.json();
      if (!jobId) throw new Error("No job ID returned");

      const maxAttempts = 160;
      let attempts = 0;

      const poll = async () => {
        attempts++;
        if (attempts > maxAttempts) {
          if (callback) callback(false, "Generation timed out", null);
          return;
        }
        try {
          const statusResponse = await authedFetch(`${apiUrl}api/jobs/${jobId}`);
          if (!statusResponse.ok) throw new Error("Failed to check job status");
          const jobData = await statusResponse.json();
          const status = jobData?.status;
          if (status === "completed") {
            const url = jobData?.result?.images?.[0]?.url || jobData?.result?.url || null;
            if (callback) callback(true, null, url, null, jobId);
          } else if (status === "failed") {
            if (callback) callback(false, "Generation failed", null);
          } else {
            setTimeout(poll, 3000);
          }
        } catch (pollError) {
          if (callback) callback(false, pollError.message, null);
        }
      };

      setTimeout(poll, 3000);
    } catch (error) {
      console.error("Error generating image with text:", error);
      if (callback) callback(false, error.message, null);
    }
  }

  static async generateImageWithReference(params, callback) {
    const {
      image,
      provider = "flux",
      prompt = "",
      orientation = "landscape",
      textOverlays = [],
      idealStudioTrainingId,
      resolution,
      sceneIndex,
      boxId,
      navId,
    } = params;

    const apiUrl = process.env.MW_API_URL;
    if (!apiUrl) {
      if (callback) callback(false, "API URL not configured", null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", image);
      formData.append("provider", provider);
      if (orientation) formData.append("orientation", orientation);
      if (prompt) formData.append("prompt", prompt);
      if (textOverlays.length > 0) formData.append("textOverlays", JSON.stringify(textOverlays));
      if (idealStudioTrainingId) formData.append("idealStudioTrainingId", idealStudioTrainingId);
      if (resolution) formData.append("resolution", resolution);
      // Widget routing hints — job completion'da geri echo edilir.
      if (sceneIndex !== undefined && sceneIndex !== null)
        formData.append("sceneIndex", String(sceneIndex));
      if (boxId) formData.append("boxId", boxId);
      if (navId) formData.append("navId", navId);

      const response = await authedFetch(
        `${apiUrl}api/image-editing/generate-with-style-reference-upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        let errBody = null;
        try {
          errBody = await response.json();
        } catch {
          /* non-JSON body */
        }
        const errorMessage = errBody?.message || `HTTP error! status: ${response.status}`;
        if (callback) callback(false, errorMessage, null, errBody);
        return;
      }

      const { jobId } = await response.json();
      if (!jobId) throw new Error("No job ID returned");

      const maxAttempts = 160;
      let attempts = 0;

      const poll = async () => {
        attempts++;
        if (attempts > maxAttempts) {
          if (callback) callback(false, "Generation timed out", null);
          return;
        }
        try {
          const statusResponse = await authedFetch(`${apiUrl}api/jobs/${jobId}`);
          if (!statusResponse.ok) throw new Error("Failed to check job status");
          const jobData = await statusResponse.json();
          const status = jobData?.status;
          if (status === "completed") {
            const url =
              jobData?.result?.images?.[0]?.url ||
              jobData?.result?.details?.generatedImages?.[0]?.url ||
              jobData?.result?.url ||
              null;
            if (callback) callback(true, null, url, null, jobId);
          } else if (status === "failed") {
            if (callback) callback(false, "Generation failed", null);
          } else {
            setTimeout(poll, 3000);
          }
        } catch (pollError) {
          if (callback) callback(false, pollError.message, null);
        }
      };

      setTimeout(poll, 3000);
    } catch (error) {
      if (callback) callback(false, error.message, null);
    }
  }

  static async generateImageWithStyle(params, callback) {
    const {
      prompt,
      provider = "flux",
      orientation = "square",
      imageCount = 1,
      customStyle,
      negativePrompt,
      imagePromptLibraryId,
      quality = "hd",
      idealStudioTrainingId,
      resolution,
      sceneIndex,
    } = params;

    const apiUrl = process.env.MW_API_URL;
    if (!apiUrl) {
      if (callback) callback(false, "API URL not configured", null);
      return;
    }

    try {
      const body = {
        prompt,
        provider,
        orientation,
        imageCount,
        quality: quality === "hd" ? "hd" : "standard",
      };

      if (customStyle) body.customStyle = customStyle;
      if (negativePrompt) body.negativePrompt = negativePrompt;
      if (imagePromptLibraryId !== undefined) body.imagePromptLibraryId = imagePromptLibraryId;
      if (idealStudioTrainingId) body.idealStudioTrainingId = idealStudioTrainingId;
      if (resolution) body.resolution = resolution;
      // Widget routing hint — tells the backend which scene this image is for.
      if (sceneIndex !== undefined && sceneIndex !== null) body.sceneIndex = String(sceneIndex);

      const response = await authedFetch(`${apiUrl}api/ideal-studio/text-to-image-async`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errBody = null;
        try {
          errBody = await response.json();
        } catch {
          /* non-JSON body */
        }
        const errorMessage = errBody?.message || `HTTP error! status: ${response.status}`;
        if (callback) callback(false, errorMessage, null, errBody);
        return;
      }

      const result = await response.json();

      // Check if response has direct images array or uses job pattern
      if (result.images && Array.isArray(result.images) && result.images.length > 0) {
        // Direct response with images
        const url = result.images[0].url;
        if (callback) callback(true, null, url);
        return;
      }

      // Job-based polling pattern
      const { jobId } = result;
      if (!jobId) throw new Error("No job ID or images returned");

      const maxAttempts = 160;
      let attempts = 0;

      const poll = async () => {
        attempts++;
        if (attempts > maxAttempts) {
          if (callback) callback(false, "Generation timed out", null);
          return;
        }
        try {
          const statusResponse = await authedFetch(`${apiUrl}api/jobs/${jobId}`);
          if (!statusResponse.ok) throw new Error("Failed to check job status");
          const jobData = await statusResponse.json();
          const status = jobData?.status;
          if (status === "completed") {
            const url =
              jobData?.result?.images?.[0]?.url ||
              jobData?.result?.url ||
              jobData?.result?.details?.generatedImages?.[0]?.url ||
              null;
            if (callback) callback(true, null, url, null, jobId);
          } else if (status === "failed") {
            if (callback) callback(false, "Generation failed", null);
          } else {
            setTimeout(poll, 3000);
          }
        } catch (pollError) {
          if (callback) callback(false, pollError.message, null);
        }
      };

      setTimeout(poll, 3000);
    } catch (error) {
      console.error("Error generating image with style:", error);
      if (callback) callback(false, error.message, null);
    }
  }

  static async generateImageDirect(params, callback) {
    const {
      prompt,
      provider = "flux",
      improvePrompt = false,
      orientation,
      resolution,
      sceneIndex,
    } = params;

    const apiUrl = process.env.MW_API_URL;
    if (!apiUrl) {
      if (callback) callback(false, "API URL not configured", null);
      return;
    }

    try {
      const body = { prompt, provider, improvePrompt };
      if (orientation) body.orientation = orientation;
      if (resolution) body.resolution = resolution;
      // Widget routing hint — tells the backend which scene this image is for.
      if (sceneIndex !== undefined && sceneIndex !== null) body.sceneIndex = String(sceneIndex);

      const response = await authedFetch(`${apiUrl}api/ideal-studio/text-to-image-async`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errBody = null;
        try {
          errBody = await response.json();
        } catch {
          /* non-JSON body */
        }
        const errorMessage = errBody?.message || `HTTP error! status: ${response.status}`;
        if (callback) callback(false, errorMessage, null, errBody);
        return;
      }

      const result = await response.json();

      if (result.images && Array.isArray(result.images) && result.images.length > 0) {
        if (callback) callback(true, null, result.images[0].url);
        return;
      }

      const { jobId } = result;
      if (!jobId) throw new Error("No job ID or images returned");

      const maxAttempts = 160;
      let attempts = 0;

      const poll = async () => {
        attempts++;
        if (attempts > maxAttempts) {
          if (callback) callback(false, "Generation timed out", null);
          return;
        }
        try {
          const statusResponse = await authedFetch(`${apiUrl}api/jobs/${jobId}`);
          if (!statusResponse.ok) throw new Error("Failed to check job status");
          const jobData = await statusResponse.json();
          const status = jobData?.status;
          if (status === "completed") {
            const url =
              jobData?.result?.images?.[0]?.url ||
              jobData?.result?.url ||
              jobData?.result?.details?.generatedImages?.[0]?.url ||
              null;
            if (callback) callback(true, null, url, null, jobId);
          } else if (status === "failed") {
            if (callback) callback(false, "Generation failed", null);
          } else {
            setTimeout(poll, 3000);
          }
        } catch (pollError) {
          if (callback) callback(false, pollError.message, null);
        }
      };

      setTimeout(poll, 3000);
    } catch (error) {
      console.error("Error generating image directly:", error);
      if (callback) callback(false, error.message, null);
    }
  }

  static async enrichPrompt(prompt) {
    const apiUrl = process.env.MW_API_URL;
    if (!apiUrl) {
      throw new Error("API URL not configured");
    }

    try {
      const response = await authedFetch(`${apiUrl}api/ideal-studio/enrich-prompt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.enriched || prompt;
    } catch (error) {
      console.error("Error enriching prompt:", error);
      throw error;
    }
  }

  static async generatePromptFromNarration(params) {
    const { narrationText, idealStudioTrainingId } = params;
    const apiUrl = process.env.MW_API_URL;
    if (!apiUrl) {
      throw new Error("API URL not configured");
    }

    try {
      const body = { prompt: narrationText };
      if (idealStudioTrainingId) {
        body.idealStudioTrainingId = idealStudioTrainingId;
      }
      const response = await authedFetch(
        `${apiUrl}api/image-editing/generate-prompt-from-narration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.prompt || narrationText;
    } catch (error) {
      console.error("Error generating prompt from narration:", error);
      throw error;
    }
  }
}

export default ImagePromptLibraryService;
